/**
 * The badge collection. The leaderboards have their own page now
 * (LeaderboardView); this one is every badge there is and which you hold,
 * with Good company matters and your community achievements beside it.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Icon } from '@/icons/Icon';
import { api } from '@/lib/api';
import type { Badge, PublicUser } from '@/lib/types';
import { cn } from '@/lib/cn';
import { go } from '@/lib/hash';
import { useSession } from '@/stores/session';
import { BadgeMark } from '@/components/BadgeMark';
import { Empty } from '@/components/ui';
import { useMinWidth } from '@/lib/viewport';

const TIERS = [
  ['mythic', 'Mythic'],
  ['epic', 'Epic'],
  ['rare', 'Rare'],
  ['common', 'Common']
] as const;

const PANEL = 'pugo-panel rounded-[calc(24px*var(--radius-mul,1))] bg-surface-1 shadow-[inset_0_0_0_1px_var(--line-soft)]';

type Period = 'week' | 'month' | 'all';
type Player = { rank: number; user: PublicUser; hosted: number; points: number };
type Board = {
  period: Period;
  players: Player[];
  total: number;
  me: Player | null;
  achievements: { parties: number; reactions: number };
};

export function BadgesView() {
  const beside = useMinWidth(1280);

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <div className="mx-auto flex w-full max-w-[1480px] items-start gap-5 px-4 py-5 sm:px-6 xl:px-8">
        <section aria-labelledby="community-title" className={cn(PANEL, '@container min-w-0 flex-1 p-5 sm:p-7 animate-[fade-in_280ms_ease-out] motion-reduce:animate-none')}>
          <div className="mb-6 flex flex-wrap items-end gap-4">
            <div className="min-w-0 flex-1">
              <h1 id="community-title" className="m-0 text-[30px] leading-tight font-extrabold tracking-[-.03em] text-ink sm:text-[38px]">Badge collection</h1>
              <p className="mt-1 mb-0 text-[16px] text-mute">Every badge there is, and the ones you have earned.</p>
            </div>
            <button
              type="button"
              onClick={() => go('leaderboard')}
              className="flex h-11 items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] bg-accent px-4 text-[14.5px] font-bold text-accent-fg outline-none transition-[background-color] hover:bg-accent-hi focus-visible:shadow-[0_0_0_3px_var(--accent-soft)]"
            >
              <Icon name="trophy" size={17} />
              Leaderboards
            </button>
          </div>
          <Collection />
        </section>

        {beside ? <GoodCompany /> : null}
      </div>
    </div>
  );
}

/** A drawn medal with ribbon tails - gold, silver, bronze. */
export function Medal({ rank }: { rank: number }) {
  const [light, dark, ribbon] = rank === 1
    ? ['#ffd65a', '#e9a616', '#e9a616']
    : rank === 2
      ? ['#e3e7ee', '#9aa3b2', '#8a93a3']
      : ['#f2b48a', '#c46f3a', '#b9652f'];
  const id = 'medal-' + rank;
  return (
    <svg width="44" height="54" viewBox="0 0 44 54" role="img" aria-label={'Rank ' + rank}>
      <defs>
        <linearGradient id={id} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor={light} />
          <stop offset="1" stopColor={dark} />
        </linearGradient>
      </defs>
      <path d="M13 32 7 50l7-3 4 6 5-17Z" fill={ribbon} opacity=".85" />
      <path d="M31 32l6 18-7-3-4 6-5-17Z" fill={ribbon} opacity=".85" />
      <circle cx="22" cy="20" r="18" fill={`url(#${id})`} />
      <circle cx="22" cy="20" r="14" fill="none" stroke="rgba(255,255,255,.45)" strokeWidth="1.5" />
      <text x="22" y="26.5" textAnchor="middle" fontSize="17" fontWeight="800" fill="rgba(40,30,10,.78)" fontFamily="Inter, system-ui, sans-serif">{rank}</text>
    </svg>
  );
}

/** Good company matters, and your own progress. */
function GoodCompany() {
  const board = useQuery({ queryKey: ['leaderboard', 'week'], queryFn: () => api.get<Board>('/api/leaderboard?period=week') });
  const a = board.data?.achievements ?? { parties: 0, reactions: 0 };

  return (
    <aside aria-labelledby="good-company-title" className={cn(PANEL, 'sticky top-0 flex w-[400px] flex-none flex-col p-7 animate-[fade-in_340ms_ease-out] motion-reduce:animate-none')}>
      <div className="relative mx-auto mt-1 mb-4 grid h-[150px] w-[220px] place-items-center">
        <span aria-hidden className="absolute inset-0 rounded-full" style={{ background: 'radial-gradient(closest-side, color-mix(in srgb, var(--warn) 22%, transparent), transparent)' }} />
        <img src="./logo.png" alt="" width={150} height={150} className="relative h-[140px] w-[140px] object-contain drop-shadow-[0_12px_20px_rgba(0,0,0,.22)] animate-[pug-rest_5s_ease-in-out_infinite] motion-reduce:animate-none" />
        <Icon name="sparkles" size={26} className="absolute top-2 left-3 text-warn" />
      </div>
      <h2 id="good-company-title" className="m-0 text-[30px] leading-tight font-bold tracking-[-.02em] text-ink">Good company matters</h2>
      <p className="mt-3 mb-0 text-[17px] leading-relaxed text-dim">
        Pugo is about more than games. It&apos;s about the people you play with. Host parties, <b className="font-bold text-ink">support others and</b> help build a welcoming community. Every game together makes a difference.
      </p>

      <div className="mt-6 border-t border-line-soft pt-6">
        <h3 className="m-0 mb-4 text-[20px] font-bold text-ink">Community achievements</h3>
        <div className="flex flex-col gap-4">
          <Achievement icon="friends" tone="var(--good)" title="Brighter Together" note="Help make 10 parties happen this month." value={a.parties} goal={10} />
          <Achievement icon="heart" tone="var(--warn)" title="Positive Vibes" note="Receive 50 friendly reactions from other players." value={a.reactions} goal={50} />
        </div>
      </div>
      <style>{`@keyframes pug-rest { 0%,100% { transform: translateY(0) } 50% { transform: translateY(-4px) } }`}</style>
    </aside>
  );
}

function Achievement({ icon, tone, title, note, value, goal }: { icon: string; tone: string; title: string; note: string; value: number; goal: number }) {
  const shown = Math.min(value, goal);
  const done = value >= goal;
  return (
    <div className="flex gap-4 rounded-[calc(18px*var(--radius-mul,1))] p-4 shadow-[inset_0_0_0_1px_var(--line-soft)] transition-shadow duration-[250ms] hover:shadow-[inset_0_0_0_1px_var(--line),var(--shadow-1)]">
      <span className="grid h-16 w-16 flex-none place-items-center rounded-[calc(14px*var(--radius-mul,1))]" style={{ background: 'color-mix(in srgb, ' + tone + ' 18%, transparent)', color: tone }}>
        <Icon name={done ? 'check' : icon} size={30} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="m-0 text-[16px] font-bold text-ink">{title}</p>
        <p className="mt-0.5 mb-0 text-[14px] leading-snug text-mute">{note}</p>
        <div className="mt-3 flex items-center gap-3">
          <span className="h-2 flex-1 overflow-hidden rounded-full bg-surface-3" role="progressbar" aria-label={title} aria-valuemin={0} aria-valuemax={goal} aria-valuenow={shown}>
            <span className="block h-full rounded-full transition-[width] duration-700 ease-[var(--ease-out)]" style={{ width: (shown / goal) * 100 + '%', background: 'var(--good)' }} />
          </span>
          <span className="flex-none text-[14px] text-dim tabular-nums">{shown} / {goal}</span>
        </div>
      </div>
    </div>
  );
}

/* -------------------------------------------------------- the collection */

function Collection() {
  const me = useSession((s) => s.me);
  const catalog = useSession((s) => s.catalog);
  const all = catalog?.badges ?? [];
  const owned = useMemo(() => new Set(me?.badges ?? []), [me?.badges]);

  if (!all.length) return <Empty title="No badges to show" note="The badge list has not loaded yet." />;

  /*
   * Earned, then locked. Earned newest first, because the one you just got is
   * the one you came to look at; locked by tier, rarest first.
   */
  const tierOrder = (b: Badge) => TIERS.findIndex(([t]) => t === b.tier);
  const earnedAt = (b: Badge) => me?.badgeMeta?.[b.id]?.at ?? 0;
  const earned = all.filter((b) => owned.has(b.id)).sort((a, b) => earnedAt(b) - earnedAt(a));
  const locked = all.filter((b) => !owned.has(b.id)).sort((a, b) => tierOrder(a) - tierOrder(b) || a.name.localeCompare(b.name));
  const grid = 'm-0 grid list-none grid-cols-2 gap-3 p-0 @2xl:grid-cols-3 @4xl:grid-cols-4 @6xl:grid-cols-5';

  return (
    <div className="flex flex-col gap-9">
      <section aria-labelledby="badges-earned-title">
        <h2 id="badges-earned-title" className="m-0 mb-3.5 text-[21px] font-bold tracking-[-.02em] text-ink">
          Earned badges <span className="text-[15px] font-semibold text-mute">({earned.length} of {all.length})</span>
        </h2>
        {earned.length ? (
          <ul className={grid}>
            {earned.map((badge, i) => <BadgeCard key={badge.id} badge={badge} owned index={i} />)}
          </ul>
        ) : (
          <Empty icon="rosette" title="Nothing earned yet" note="Badges arrive as you play - host a party, make friends, keep showing up." />
        )}
      </section>

      {locked.length ? (
        <section aria-labelledby="badges-locked-title">
          <h2 id="badges-locked-title" className="m-0 mb-3.5 text-[21px] font-bold tracking-[-.02em] text-ink">
            Locked badges <span className="text-[15px] font-semibold text-mute">({locked.length})</span>
          </h2>
          <ul className={grid}>
            {locked.map((badge, i) => <BadgeCard key={badge.id} badge={badge} owned={false} index={i} />)}
          </ul>
        </section>
      ) : (
        <p className="m-0 text-[15px] text-mute">You have every badge there is.</p>
      )}
    </div>
  );
}

function BadgeCard({ badge, owned, index }: { badge: Badge; owned: boolean; index: number }) {
  const me = useSession((s) => s.me);
  const meta = me?.badgeMeta?.[badge.id];
  const tier = TIERS.find(([t]) => t === badge.tier)?.[1] ?? badge.tier;

  return (
    <li
      style={{ animationDelay: Math.min(index, 12) * 30 + 'ms' }}
      className={cn(
        'flex flex-col items-center rounded-[calc(20px*var(--radius-mul,1))] px-3 pt-5 pb-4 text-center shadow-[inset_0_0_0_1px_var(--line-soft)]',
        'animate-[fade-in_300ms_ease-out_backwards] transition-[box-shadow,transform] duration-[250ms] ease-[var(--ease-out)] motion-reduce:animate-none',
        owned ? 'bg-surface-1 hover:-translate-y-0.5 hover:shadow-[inset_0_0_0_1px_var(--line),var(--shadow-1)] motion-reduce:hover:translate-y-0' : 'bg-surface-2'
      )}
    >
      <span className={cn('grid place-items-center', !owned && 'opacity-60 grayscale')}>
        <BadgeMark badge={badge} size={72} locked={!owned} />
      </span>
      <h3 className="mt-3 mb-0 text-[16px] font-bold text-ink">{badge.name}</h3>
      <p className="mt-1 mb-0 text-[13.5px] leading-snug text-mute">{badge.desc}</p>
      <span className="mt-auto pt-3">
        {owned ? (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-[color-mix(in_srgb,var(--good)_14%,transparent)] px-2.5 py-1 text-[13px] font-semibold text-good">
            <Icon name="check" size={14} />
            Earned
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-surface-3 px-2.5 py-1 text-[13px] font-semibold text-dim">
            <Icon name="lock" size={13} />
            {badge.auto ? 'Not earned yet' : 'Awarded by staff'}
          </span>
        )}
      </span>
      <span className="mt-2 text-[12.5px] text-mute">
        {tier}
        {owned && meta ? ' · ' + new Date(meta.at).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
      </span>
    </li>
  );
}
